<template>
  <div class="pagina-registro">
    <div class="tarjeta-registro">
      <img
        :src="require('@/assets/logo.png')"
        alt="Bahiol"
        class="logo"
      >

      <h1 class="titulo">Crear cuenta</h1>
      <p class="subtitulo">Registra tu negocio y empieza a mostrarlo</p>

      <v-form @submit.prevent="registrar">
        <div class="grupo">Tus datos</div>

        <div class="grid-dos">
          <v-text-field
            v-model="nombre"
            class="fg-blue"
            label="Nombres"
            variant="outlined"
            density="comfortable"
            autocomplete="given-name"
            :error-messages="errorNombre"
          />
          <v-text-field
            v-model="apellidos"
            class="fg-blue"
            label="Apellidos (opcional)"
            variant="outlined"
            density="comfortable"
            autocomplete="family-name"
          />
        </div>

        <div class="grid-dos">
          <v-text-field
            v-model="correo"
            class="fg-blue"
            label="Correo"
            type="email"
            variant="outlined"
            density="comfortable"
            autocomplete="email"
            :error-messages="errorCorreo"
          />
          <v-text-field
            v-model.number="edad"
            class="fg-blue"
            label="Edad"
            type="number"
            min="18"
            variant="outlined"
            density="comfortable"
            :error-messages="errorEdad"
          />
        </div>

        <!-- Verificacion del correo: el codigo se pide aqui y viaja junto con
             el resto del formulario. El usuario no cambia de pagina, solo abre
             su correo en otra pestana -->
        <div class="grupo">Verifica tu correo</div>

        <div class="fila-codigo">
          <v-text-field
            v-model="codigo"
            class="fg-blue"
            label="Codigo del correo"
            variant="outlined"
            density="comfortable"
            maxlength="6"
            inputmode="numeric"
            placeholder="000000"
            :disabled="!codigoEnviado"
            :error-messages="errorCodigo"
          />
          <v-btn
            color="primary"
            variant="tonal"
            size="large"
            class="btn-codigo"
            :loading="enviandoCodigo"
            :disabled="!correoValido || esperaReenvio > 0"
            @click="pedirCodigo"
          >
            {{ textoBotonCodigo }}
          </v-btn>
        </div>

        <v-alert
          v-if="avisoCodigo"
          type="success"
          variant="tonal"
          density="compact"
          class="mb-2"
        >
          {{ avisoCodigo }}
        </v-alert>

        <div class="grupo">Datos de acceso</div>

        <v-text-field
          v-model="usuario"
          class="fg-blue"
          label="Usuario"
          variant="outlined"
          density="comfortable"
          autocomplete="username"
          prepend-inner-icon="mdi-account-outline"
          :error-messages="errorUsuario"
        />

        <v-text-field
          v-model="clave"
          class="fg-blue"
          label="Contraseña"
          variant="outlined"
          density="comfortable"
          autocomplete="new-password"
          prepend-inner-icon="mdi-lock-outline"
          :type="verClave ? 'text' : 'password'"
          :append-inner-icon="verClave ? 'mdi-eye-off' : 'mdi-eye'"
          hint="Mínimo 5 caracteres, con al menos una letra y un número"
          persistent-hint
          :error-messages="errorClave"
          @click:append-inner="verClave = !verClave"
        />

        <v-text-field
          v-model="claveConfirmada"
          class="fg-blue mt-3"
          label="Repetir contraseña"
          variant="outlined"
          density="comfortable"
          autocomplete="new-password"
          prepend-inner-icon="mdi-lock-check-outline"
          :type="verClave ? 'text' : 'password'"
          :error-messages="errorConfirmar"
        />

        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-2"
        >
          {{ error }}
        </v-alert>

        <v-btn
          type="submit"
          color="primary"
          size="large"
          block
          class="mt-4"
          :loading="registrando"
        >
          Crear cuenta
        </v-btn>
      </v-form>

      <div class="pie">
        ¿Ya tienes cuenta?
        <a
          class="enlace"
          @click="$router.push({ name: 'LoginView' })"
        >Inicia sesión</a>
      </div>
    </div>
  </div>
</template>

<script>
import apisUsers from '@/apis/apisUsers';
import { REQUERIDO, CORREO, MAYOR_DE_EDAD, CLAVE } from '@/utils/validaciones';

export default {
  name: 'RegistreView',
  data() {
    return {
      usuario: '',
      clave: '',
      claveConfirmada: '',
      nombre: '',
      apellidos: '',
      correo: '',
      edad: null,
      verClave: false,
      registrando: false,
      error: '',
      // Hasta el primer envio no se muestra ningun error
      intentoEnvio: false,

      // Verificacion del correo
      codigo: '',
      codigoEnviado: false,
      correoDelCodigo: '',
      enviandoCodigo: false,
      avisoCodigo: '',
      esperaReenvio: 0,
      temporizador: null
    }
  },
  computed: {
    // Los mensajes se calculan aqui en vez de usar :rules del v-form. Con
    // :rules el mensaje se quedaba pegado despues de corregir el campo: la
    // revalidacion por campo no era fiable. Calculandolos, la reactividad de
    // Vue garantiza que se actualicen en cuanto cambia el modelo.
    // Solo se muestran despues del primer intento de envio, para no regañar
    // al usuario mientras todavia esta llenando el formulario.
    errorNombre() { return this.mensaje(REQUERIDO(this.nombre)) },
    errorCorreo() { return this.mensaje(REQUERIDO(this.correo), CORREO(this.correo)) },
    errorEdad() { return this.mensaje(REQUERIDO(this.edad), MAYOR_DE_EDAD(this.edad)) },
    errorUsuario() { return this.mensaje(REQUERIDO(this.usuario)) },
    errorClave() { return this.mensaje(REQUERIDO(this.clave), CLAVE(this.clave)) },
    errorConfirmar() {
      return this.mensaje(
        REQUERIDO(this.claveConfirmada),
        this.claveConfirmada === this.clave || 'Las contraseñas no coinciden'
      )
    },
    errorCodigo() {
      return this.mensaje(REQUERIDO(this.codigo))
    },
    correoValido() {
      return CORREO(this.correo) === true
    },
    textoBotonCodigo() {
      if (this.esperaReenvio > 0) {
        return `Reenviar en ${this.esperaReenvio}s`
      }
      return this.codigoEnviado ? 'Reenviar' : 'Enviar codigo'
    },
    hayErrores() {
      return [REQUERIDO(this.nombre), REQUERIDO(this.correo), CORREO(this.correo),
        REQUERIDO(this.edad), MAYOR_DE_EDAD(this.edad), REQUERIDO(this.usuario),
        REQUERIDO(this.clave), CLAVE(this.clave), REQUERIDO(this.claveConfirmada),
        this.claveConfirmada === this.clave, REQUERIDO(this.codigo)].some(r => r !== true)
    }
  },
  watch: {
    // El codigo esta atado al correo al que se envio. Si el usuario lo cambia,
    // ese codigo ya no le sirve y el backend lo rechazaria: se limpia para que
    // se de cuenta aqui y no al final del formulario.
    correo(nuevo) {
      if (this.codigoEnviado && nuevo.trim().toLowerCase() !== this.correoDelCodigo) {
        this.codigoEnviado = false
        this.codigo = ''
        this.avisoCodigo = ''
        // La espera tambien se reinicia: el limite del backend es POR CORREO,
        // asi que para una direccion distinta no aplica y dejar la cuenta atras
        // corriendo bloquearia una peticion que si esta permitida
        clearInterval(this.temporizador)
        this.esperaReenvio = 0
      }
    }
  },
  beforeUnmount() {
    clearInterval(this.temporizador)
  },
  methods: {
    /** Devuelve el primer fallo, o [] si todo bien o aun no se intento enviar. */
    mensaje(...resultados) {
      if (!this.intentoEnvio) {
        return []
      }
      const fallo = resultados.find(r => r !== true)
      return fallo ? [fallo] : []
    },
    pedirCodigo() {
      this.error = ''
      this.avisoCodigo = ''
      this.enviandoCodigo = true

      const correo = this.correo.trim().toLowerCase()

      apisUsers.solicitarCodigo(correo, 'registro')
        .then(() => {
          this.codigoEnviado = true
          this.correoDelCodigo = correo
          this.avisoCodigo = `Te enviamos un codigo a ${correo}. Revisa tambien la carpeta de spam.`
          this.iniciarEsperaReenvio()
        })
        .catch(err => {
          // Aqui cae tanto "ese correo ya tiene una cuenta" como el limite de
          // reenvios, y los dos le sirven al usuario tal cual vienen
          this.error = err.message
        })
        .finally(() => {
          this.enviandoCodigo = false
        })
    },
    /** El backend rechaza otro codigo antes de 60s; se refleja en el boton. */
    iniciarEsperaReenvio() {
      clearInterval(this.temporizador)
      this.esperaReenvio = 60

      this.temporizador = setInterval(() => {
        this.esperaReenvio--
        if (this.esperaReenvio <= 0) {
          clearInterval(this.temporizador)
        }
      }, 1000)
    },
    async registrar() {
      this.error = ''
      this.intentoEnvio = true

      if (this.hayErrores) {
        return
      }

      this.registrando = true

      try {
        await apisUsers.save(
          this.usuario.trim(), this.clave, this.nombre.trim(),
          this.apellidos.trim(), this.correo.trim().toLowerCase(), this.edad, '', '',
          this.codigo.trim()
        )
      } catch (err) {
        // El backend manda el motivo concreto: usuario repetido, correo ya
        // registrado, etc.
        this.error = err.message
        this.registrando = false
        return
      }

      // La cuenta YA quedo creada. Si el ingreso automatico falla, no se puede
      // dejar al usuario en el formulario: reintentaria y le diria que el
      // usuario ya existe. Se le avisa y se le manda a iniciar sesion.
      try {
        await apisUsers.login(this.usuario.trim(), this.clave)
        this.$router.push({ name: 'home' })
      } catch (err) {
        this.$router.push({ name: 'LoginView' })
      } finally {
        this.registrando = false
      }
    }
  }
}
</script>

<style scoped>
.pagina-registro {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 140px);
  padding: 16px;
  background: linear-gradient(160deg, #dbeafe 0%, #eff6ff 45%, #f8fafc 100%);
}

.tarjeta-registro {
  width: 100%;
  max-width: 460px;
  background: #fff;
  border-radius: 16px;
  padding: 28px 24px 24px;
  box-shadow: 0 10px 30px rgba(25, 118, 210, 0.12);
}

.logo {
  height: 44px;
  display: block;
  margin: 0 auto 14px;
}

.titulo {
  font-size: 1.35rem;
  font-weight: 700;
  color: #111;
  text-align: center;
  margin: 0;
}

.subtitulo {
  font-size: 0.85rem;
  color: #777;
  text-align: center;
  margin: 4px 0 20px;
}

.grupo {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #1976d2;
  margin: 6px 0 10px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e3e8ef;
}

/* Una columna en celular: dos campos por fila en pantallas chicas aprietan */
.grid-dos {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0 10px;
}

/* El boton va al lado del campo, no debajo: en una fila estrecha el usuario
   ve de inmediato que el codigo se pide desde ahi */
.fila-codigo {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: start;
}
.btn-codigo {
  margin-top: 4px;
  white-space: nowrap;
}

.pie {
  margin-top: 18px;
  text-align: center;
  font-size: 0.85rem;
  color: #666;
}

.enlace {
  color: #1976d2;
  font-weight: 600;
  cursor: pointer;
  margin-left: 2px;
}
.enlace:hover {
  text-decoration: underline;
}

@media (min-width: 700px) {
  .tarjeta-registro {
    max-width: 620px;
    padding: 36px 40px 28px;
  }
  .grid-dos {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
